import type { FormSettings } from '~/form-settings'

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

export const DEFAULT_RECAPTCHA_SITE_KEY = '6Lcm8HwrAAAAAOXZEAPUE2X3J93pN1oXpourU7ni'

export type RecaptchaConfig = {
  siteKey: string
  action: string
  minScore: number
}

export function getRecaptchaConfig (
  settings?: Pick<FormSettings, 'recaptcha'>
): RecaptchaConfig | null {
  const recaptcha = settings?.recaptcha

  if (!recaptcha?.enabled) return null

  return {
    siteKey: recaptcha.siteKey || DEFAULT_RECAPTCHA_SITE_KEY,
    action: recaptcha.action || 'submit',
    minScore: recaptcha.minScore ?? 0.5
  }
}

function waitForRecaptcha (): Promise<void> {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + 10000

    const check = () => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => resolve())
        return
      }

      if (Date.now() >= deadline) {
        reject(new Error('reCAPTCHA did not become ready'))
        return
      }

      window.setTimeout(check, 100)
    }

    check()
  })
}

export async function loadRecaptcha (siteKey: string): Promise<void> {
  if (typeof window === 'undefined') {
    throw new Error('reCAPTCHA can only load in a browser')
  }

  if (window.grecaptcha?.ready) {
    await waitForRecaptcha()
    return
  }

  const existingScript = document.querySelector('script[data-donation-recaptcha]')
  if (!existingScript) {
    const script = document.createElement('script')
    script.async = true
    script.defer = true
    script.dataset.donationRecaptcha = 'true'
    script.dataset.osano = 'ESSENTIAL'
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    document.head.appendChild(script)
  }

  await waitForRecaptcha()
}

export async function executeRecaptcha (
  config: RecaptchaConfig
): Promise<string> {
  await loadRecaptcha(config.siteKey)

  const token = await window.grecaptcha!.execute(config.siteKey, {
    action: config.action
  })

  if (!token) {
    throw new Error('reCAPTCHA token generation failed')
  }

  return token
}
